import { useMemo } from 'react';
import { useSalesforceData } from './useSalesforceData';
import { RECORD_TYPES, EXCLUDE_DEACTIVATED_FIRMS, EXCLUDE_DEACTIVATED_FIRMS_REL } from '../constants';

// Firms now come from Encore_Firm__c (the synced usage object). It is not 1:1
// with firms, so rows are de-duped client-side by EEP_Firm_ID__c, which is also
// used as the join key (`Id`) so downstream filtering/joins stay unchanged.
// Firm usage fields read directly; CRM/churn fields live only on Account and are
// reached via Account__r (every Encore_Firm__c row has Account__c populated).
const ACCOUNTS_QUERY = `SELECT Id, EEP_Firm_ID__c, Name, EEP_Firm_Created_Date__c, EEP_Total_Advisors__c, EEP_Total_Plan_Summaries_Sold__c, Total_Plans_Sold__c, First_Plan_Date__c, Second_Plan_Date__c, EEP_Trust_Based_Plan_Credits__c, EEP_Will_Based_Plan_Credits__c, Plan_Summary_Plan_Credits__c, Firm_CRD__c, Website__c, Account__r.EEP_Affiliation_Code__c, Account__r.Total_Revenue__c, Account__r.Seventh_Plan_Date__c, Account__r.Last_Plan_Date__c, Account__r.Last_Spend_Date__c, Account__r.Number_of_Incomplete_Plans__c, Account__r.Churn_Date__c, Account__r.Churn_Reason__c FROM Encore_Firm__c WHERE EEP_Firm_ID__c != null${EXCLUDE_DEACTIVATED_FIRMS} ORDER BY Name`;

// Orders (TRANSACTION record type) had their Account lookup replaced by
// Encore_Firm__c, so traverse Encore_Firm__r. Opps join to firms by EEP_Firm_ID__c.
const OPPS_QUERY = `SELECT Encore_Firm__r.Name, Encore_Firm__r.EEP_Firm_ID__c, Encore_Firm__r.EEP_Firm_Created_Date__c, Plan_Type__c, Plan_Created_Date__c, Submitted_Date__c, CloseDate, IsWon, Is_Plan_Amendment__c, Include_Mobile_Notary_Service__c, Include_Attorney_Review_Letter__c, Include_Attorney_Review_Letter_Call__c, Include_Print_Bind_Tab_Ship_Service__c, Advisor__r.FirstName, Advisor__r.LastName, Advisor__r.User_Type__c FROM Opportunity WHERE RecordTypeId = '${RECORD_TYPES.TRANSACTION}'${EXCLUDE_DEACTIVATED_FIRMS_REL}`;

export interface AccountRecord {
  Id: string; // = EEP_Firm_ID__c (stable firm identifier + join key)
  Name: string;
  EEP_Firm_ID__c: string | null;
  EEP_Affiliation_Code__c: string | null;
  EEP_Account_Created_Date__c: string | null; // sourced from Encore_Firm__c.EEP_Firm_Created_Date__c
  EEP_Total_Advisors__c: number | null;
  EEP_Total_Plan_Summaries_Sold__c: number | null;
  Total_Revenue__c: number | null;
  Total_Plans_Sold__c: number | null;
  First_Plan_Date__c: string | null;
  Second_Plan_Date__c: string | null;
  Seventh_Plan_Date__c: string | null;
  Last_Plan_Date__c: string | null;
  Last_Spend_Date__c: string | null;
  EEP_Trust_Based_Plan_Credits__c: number | null;
  EEP_Will_Based_Plan_Credits__c: number | null;
  Plan_Summary_Plan_Credits__c: number | null;
  Number_of_Incomplete_Plans__c: number | null;
  Firm_CRD__c: string | null;
  Website__c: string | null;
  Churn_Date__c: string | null;
  Churn_Reason__c: string | null;
  cohortYear: string | null;
}

export interface OpportunityRecord {
  AccountId: string; // = firm EEP_Firm_ID__c (join key)
  accountName: string;
  firmId: string | null;
  accountCreatedDate: string | null;
  cohortYear: string | null;
  planType: string | null;
  planCreatedDate: string | null;
  submittedDate: string | null;
  closeDate: string | null;
  isWon: boolean;
  isAmendment: boolean;
  includeNotary: boolean;
  includeAttorneyLetter: boolean;
  includeAttorneyCall: boolean;
  includePrintBind: boolean;
  advisorName: string;
  userType: string | null;
}

const num = (v: unknown): number | null => (v != null ? Number(v) : null);
const str = (v: unknown): string | null => (v as string) || null;

/** Read a possibly-nested relationship field, e.g. rel(r, 'Account__r.Total_Revenue__c'). */
function rel(r: Record<string, any>, path: string): unknown {
  if (r[path] != null) return r[path];
  const [relName, field] = path.split('.');
  return r[relName]?.[field];
}

export function useAccountDashboardData() {
  const { data: rawAccounts, loading: accountsLoading, error: accountsError, refetch: refetchAccounts } = useSalesforceData(ACCOUNTS_QUERY);
  const { data: rawOpps, loading: oppsLoading, error: oppsError, refetch: refetchOpps } = useSalesforceData(OPPS_QUERY);

  const accounts = useMemo<AccountRecord[]>(() => {
    const seen = new Set<string>();
    const out: AccountRecord[] = [];
    for (const r of rawAccounts as any[]) {
      const firmId = (r.EEP_Firm_ID__c as string) || null;
      if (!firmId || seen.has(firmId)) continue; // de-dupe seat/sync-dupe rows
      seen.add(firmId);
      const createdDate = str(r.EEP_Firm_Created_Date__c);
      out.push({
        Id: firmId,
        Name: r.Name as string,
        EEP_Firm_ID__c: firmId,
        EEP_Affiliation_Code__c: str(rel(r, 'Account__r.EEP_Affiliation_Code__c')),
        EEP_Account_Created_Date__c: createdDate,
        EEP_Total_Advisors__c: num(r.EEP_Total_Advisors__c),
        EEP_Total_Plan_Summaries_Sold__c: num(r.EEP_Total_Plan_Summaries_Sold__c),
        Total_Revenue__c: num(rel(r, 'Account__r.Total_Revenue__c')),
        Total_Plans_Sold__c: num(r.Total_Plans_Sold__c),
        First_Plan_Date__c: str(r.First_Plan_Date__c),
        Second_Plan_Date__c: str(r.Second_Plan_Date__c),
        Seventh_Plan_Date__c: str(rel(r, 'Account__r.Seventh_Plan_Date__c')),
        Last_Plan_Date__c: str(rel(r, 'Account__r.Last_Plan_Date__c')),
        Last_Spend_Date__c: str(rel(r, 'Account__r.Last_Spend_Date__c')),
        EEP_Trust_Based_Plan_Credits__c: num(r.EEP_Trust_Based_Plan_Credits__c),
        EEP_Will_Based_Plan_Credits__c: num(r.EEP_Will_Based_Plan_Credits__c),
        Plan_Summary_Plan_Credits__c: num(r.Plan_Summary_Plan_Credits__c),
        Number_of_Incomplete_Plans__c: num(rel(r, 'Account__r.Number_of_Incomplete_Plans__c')),
        Firm_CRD__c: str(r.Firm_CRD__c),
        Website__c: str(r.Website__c),
        Churn_Date__c: str(rel(r, 'Account__r.Churn_Date__c')),
        Churn_Reason__c: str(rel(r, 'Account__r.Churn_Reason__c')),
        cohortYear: createdDate ? String(new Date(createdDate).getFullYear()) : null,
      });
    }
    return out;
  }, [rawAccounts]);

  const opportunities = useMemo<OpportunityRecord[]>(() => {
    return (rawOpps as any[]).map((r) => {
      const firstName = r['Advisor__r.FirstName'] || r.Advisor__r?.FirstName || '';
      const lastName = r['Advisor__r.LastName'] || r.Advisor__r?.LastName || '';
      const advisorName = [firstName, lastName].filter(Boolean).join(' ') || 'Unknown';
      const firmId = (r['Encore_Firm__r.EEP_Firm_ID__c'] || r.Encore_Firm__r?.EEP_Firm_ID__c || null) as string | null;
      const accountCreatedDate = (r['Encore_Firm__r.EEP_Firm_Created_Date__c'] || r.Encore_Firm__r?.EEP_Firm_Created_Date__c || null) as string | null;
      return {
        AccountId: (firmId || '') as string,
        accountName: (r['Encore_Firm__r.Name'] || r.Encore_Firm__r?.Name || '') as string,
        firmId,
        accountCreatedDate,
        cohortYear: accountCreatedDate ? String(new Date(accountCreatedDate).getFullYear()) : null,
        planType: str(r.Plan_Type__c),
        planCreatedDate: str(r.Plan_Created_Date__c),
        submittedDate: str(r.Submitted_Date__c),
        closeDate: str(r.CloseDate),
        isWon: Boolean(r.IsWon),
        isAmendment: Boolean(r.Is_Plan_Amendment__c),
        includeNotary: Boolean(r.Include_Mobile_Notary_Service__c),
        includeAttorneyLetter: Boolean(r.Include_Attorney_Review_Letter__c),
        includeAttorneyCall: Boolean(r.Include_Attorney_Review_Letter_Call__c),
        includePrintBind: Boolean(r.Include_Print_Bind_Tab_Ship_Service__c),
        advisorName,
        userType: (r['Advisor__r.User_Type__c'] || r.Advisor__r?.User_Type__c || null) as string | null,
      };
    });
  }, [rawOpps]);

  const loading = accountsLoading || oppsLoading;
  const error = accountsError || oppsError;

  function refetch() {
    refetchAccounts();
    refetchOpps();
  }

  return { accounts, opportunities, loading, error, refetch };
}
